import { createTheme, Theme } from '@mui/material/styles';

export interface ThemeColors {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  gradientStart: string;
  gradientEnd: string;
}

export interface SemanticColors {
  error: { main: string; light: string; dark: string };
  warning: { main: string; light: string; dark: string };
  success: { main: string; light: string; dark: string };
  info: { main: string; light: string; dark: string };
}

// Fixed BEO Software palette. To swap themes later, edit only this block.
export const colors: ThemeColors = {
  primary: '#F26F21',
  primaryLight: '#FF8F4D',
  primaryDark: '#C95611',
  secondary: '#F5B800',
  secondaryLight: '#FFCB47',
  secondaryDark: '#C99500',
  gradientStart: '#F26F21',
  gradientEnd: '#F5B800',
};

// Extended brand surface tokens used by bespoke layout components.
export const brand = {
  yellow: '#FCB813',
  orange: '#F26F21',
  black: '#1A1A1A',
  sidebarSurface: '#F1F2F2',
  sidebarActiveBg: '#FFF8E1',
  dropZoneBg: '#F5F5F5',
  borderSubtle: '#E0E0E0',
  textPrimary: '#1A1A1A',
  textSecondary: '#6B6B6B',
  textMuted: '#9E9E9E',
  success: '#7DCE82',
  warning: '#F5B800',
  error: '#E53935',
  // Pill text colors paired with success/warning/error backgrounds:
  successText: '#1A4D1E',
  warningText: '#5A4400',
  errorText: '#C62828',
  errorSoftBg: '#FFEBEE',
};

export const radii = {
  base: 8,
  large: 12,
  pill: 999,
};

// Shared `sx` snippets that match DocumentProcessing's primary CTA chrome —
// black background, white text, branded disabled state. Import as
// `import { primaryButtonSx } from '../theme'` and pass via
// `sx={primaryButtonSx}` so every app's primary action looks the same.
export const primaryButtonSx = {
  backgroundColor: brand.black,
  color: '#fff',
  py: 1.2,
  px: 3,
  fontWeight: 600,
  borderRadius: 1,
  textTransform: 'none' as const,
  '&:hover': { backgroundColor: '#000' },
  '&.Mui-disabled': { backgroundColor: '#3A3A3A', color: '#888' },
};

export const isDarkMode = false;

const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToHex = (h: number, s: number, l: number): string => {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const generateColorVariations = (hex: string): { light: string; dark: string } => {
  const hsl = hexToHsl(hex);
  const light = hslToHex(hsl.h, Math.max(0, hsl.s - 10), Math.min(100, hsl.l + 15));
  const dark = hslToHex(hsl.h, Math.min(100, hsl.s + 10), Math.max(0, hsl.l - 15));
  return { light, dark };
};

const blendColors = (color1: string, color2: string, ratio: number = 0.5): string => {
  const hsl1 = hexToHsl(color1);
  const hsl2 = hexToHsl(color2);

  let hueDiff = hsl2.h - hsl1.h;
  if (hueDiff > 180) hueDiff -= 360;
  if (hueDiff < -180) hueDiff += 360;
  let blendedHue = hsl1.h + hueDiff * ratio;
  if (blendedHue < 0) blendedHue += 360;
  if (blendedHue >= 360) blendedHue -= 360;

  const blendedSat = hsl1.s + (hsl2.s - hsl1.s) * ratio;
  const blendedLight = hsl1.l + (hsl2.l - hsl1.l) * ratio;

  return hslToHex(blendedHue, blendedSat, blendedLight);
};

const buildSemanticColors = (themeColors: ThemeColors): SemanticColors => {
  const primaryHsl = hexToHsl(themeColors.primary);
  const secondaryHsl = hexToHsl(themeColors.secondary);

  const baseError = '#f44336';
  const baseWarning = '#ff9800';
  const baseSuccess = '#4caf50';
  const baseInfo = '#2196f3';

  const blendRatio = 0.15;

  const getBlendColor = (baseHue: number): string => {
    const primaryDist = Math.min(Math.abs(primaryHsl.h - baseHue), 360 - Math.abs(primaryHsl.h - baseHue));
    const secondaryDist = Math.min(Math.abs(secondaryHsl.h - baseHue), 360 - Math.abs(secondaryHsl.h - baseHue));
    return primaryDist < secondaryDist ? themeColors.primary : themeColors.secondary;
  };

  const errorMain = blendColors(baseError, getBlendColor(0), blendRatio);
  const errorVars = generateColorVariations(errorMain);

  const warningMain = blendColors(baseWarning, getBlendColor(38), blendRatio);
  const warningVars = generateColorVariations(warningMain);

  const successMain = blendColors(baseSuccess, getBlendColor(120), blendRatio);
  const successVars = generateColorVariations(successMain);

  let infoMain: string;
  if (primaryHsl.h >= 180 && primaryHsl.h <= 240) {
    infoMain = themeColors.primary;
  } else if (secondaryHsl.h >= 180 && secondaryHsl.h <= 240) {
    infoMain = themeColors.secondary;
  } else {
    infoMain = blendColors(baseInfo, getBlendColor(210), blendRatio);
  }
  const infoVars = generateColorVariations(infoMain);

  return {
    error: { main: errorMain, light: errorVars.light, dark: errorVars.dark },
    warning: { main: warningMain, light: warningVars.light, dark: warningVars.dark },
    success: { main: successMain, light: successVars.light, dark: successVars.dark },
    info: { main: infoMain, light: infoVars.light, dark: infoVars.dark },
  };
};

export const semanticColors: SemanticColors = buildSemanticColors(colors);

const theme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: '#fff',
    },
    secondary: {
      main: colors.secondary,
      light: colors.secondaryLight,
      dark: colors.secondaryDark,
      contrastText: '#fff',
    },
    error: semanticColors.error,
    warning: semanticColors.warning,
    info: semanticColors.info,
    success: semanticColors.success,
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      'Poppins',
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Helvetica Neue',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.2 },
    h2: { fontWeight: 600, fontSize: '2rem', lineHeight: 1.3 },
    h3: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.4 },
    h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
    h5: { fontWeight: 500, fontSize: '1.125rem', lineHeight: 1.5 },
    h6: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    button: { fontWeight: 500, textTransform: 'none' as const },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)' },
        elevation1: { boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' },
        elevation2: { boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)' },
        elevation3: { boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          '&.MuiChip-colorSuccess': {
            backgroundColor: semanticColors.success.main,
            color: '#fff',
            '&:hover': { backgroundColor: semanticColors.success.dark },
          },
          '&.MuiChip-colorError': {
            backgroundColor: semanticColors.error.main,
            color: '#fff',
            '&:hover': { backgroundColor: semanticColors.error.dark },
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: semanticColors.warning.main,
            color: '#fff',
            '&:hover': { backgroundColor: semanticColors.warning.dark },
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: semanticColors.info.main,
            color: '#fff',
            '&:hover': { backgroundColor: semanticColors.info.dark },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
        standardSuccess: {
          backgroundColor: `${semanticColors.success.light}30`,
          color: semanticColors.success.dark,
          border: `1px solid ${semanticColors.success.main}40`,
          '& .MuiAlert-icon': { color: semanticColors.success.main },
        },
        standardError: {
          backgroundColor: `${semanticColors.error.light}30`,
          color: semanticColors.error.dark,
          border: `1px solid ${semanticColors.error.main}40`,
          '& .MuiAlert-icon': { color: semanticColors.error.main },
        },
        standardWarning: {
          backgroundColor: `${semanticColors.warning.light}30`,
          color: semanticColors.warning.dark,
          border: `1px solid ${semanticColors.warning.main}40`,
          '& .MuiAlert-icon': { color: semanticColors.warning.main },
        },
        standardInfo: {
          backgroundColor: `${semanticColors.info.light}30`,
          color: semanticColors.info.dark,
          border: `1px solid ${semanticColors.info.main}40`,
          '& .MuiAlert-icon': { color: semanticColors.info.main },
        },
        filledSuccess: {
          backgroundColor: semanticColors.success.main,
          '&:hover': { backgroundColor: semanticColors.success.dark },
        },
        filledError: {
          backgroundColor: semanticColors.error.main,
          '&:hover': { backgroundColor: semanticColors.error.dark },
        },
        filledWarning: {
          backgroundColor: semanticColors.warning.main,
          '&:hover': { backgroundColor: semanticColors.warning.dark },
        },
        filledInfo: {
          backgroundColor: semanticColors.info.main,
          '&:hover': { backgroundColor: semanticColors.info.dark },
        },
        outlinedSuccess: {
          borderColor: semanticColors.success.main,
          color: semanticColors.success.dark,
          '& .MuiAlert-icon': { color: semanticColors.success.main },
        },
        outlinedError: {
          borderColor: semanticColors.error.main,
          color: semanticColors.error.dark,
          '& .MuiAlert-icon': { color: semanticColors.error.main },
        },
        outlinedWarning: {
          borderColor: semanticColors.warning.main,
          color: semanticColors.warning.dark,
          '& .MuiAlert-icon': { color: semanticColors.warning.main },
        },
        outlinedInfo: {
          borderColor: semanticColors.info.main,
          color: semanticColors.info.dark,
          '& .MuiAlert-icon': { color: semanticColors.info.main },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          '&.MuiLinearProgress-colorSuccess': {
            backgroundColor: `${semanticColors.success.light}40`,
            '& .MuiLinearProgress-bar': { backgroundColor: semanticColors.success.main },
          },
          '&.MuiLinearProgress-colorError': {
            backgroundColor: `${semanticColors.error.light}40`,
            '& .MuiLinearProgress-bar': { backgroundColor: semanticColors.error.main },
          },
          '&.MuiLinearProgress-colorWarning': {
            backgroundColor: `${semanticColors.warning.light}40`,
            '& .MuiLinearProgress-bar': { backgroundColor: semanticColors.warning.main },
          },
          '&.MuiLinearProgress-colorInfo': {
            backgroundColor: `${semanticColors.info.light}40`,
            '& .MuiLinearProgress-bar': { backgroundColor: semanticColors.info.main },
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          '&.MuiCircularProgress-colorSuccess': { color: semanticColors.success.main },
          '&.MuiCircularProgress-colorError': { color: semanticColors.error.main },
          '&.MuiCircularProgress-colorWarning': { color: semanticColors.warning.main },
          '&.MuiCircularProgress-colorInfo': { color: semanticColors.info.main },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          '&.MuiBadge-colorSuccess': { backgroundColor: semanticColors.success.main, color: '#fff' },
          '&.MuiBadge-colorError': { backgroundColor: semanticColors.error.main, color: '#fff' },
          '&.MuiBadge-colorWarning': { backgroundColor: semanticColors.warning.main, color: '#fff' },
          '&.MuiBadge-colorInfo': { backgroundColor: semanticColors.info.main, color: '#fff' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.MuiIconButton-colorSuccess': {
            color: semanticColors.success.main,
            '&:hover': { backgroundColor: `${semanticColors.success.main}14` },
          },
          '&.MuiIconButton-colorError': {
            color: semanticColors.error.main,
            '&:hover': { backgroundColor: `${semanticColors.error.main}14` },
          },
          '&.MuiIconButton-colorWarning': {
            color: semanticColors.warning.main,
            '&:hover': { backgroundColor: `${semanticColors.warning.main}14` },
          },
          '&.MuiIconButton-colorInfo': {
            color: semanticColors.info.main,
            '&:hover': { backgroundColor: `${semanticColors.info.main}14` },
          },
        },
      },
    },
  },
});

export default theme;
